// src/components/RealBatteryHealthWidget.tsx
import React from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { Battery20, Battery50, Battery80, BatteryFull } from '@mui/icons-material';

interface Props {
  data: any[];
}

const RealBatteryHealthWidget: React.FC<Props> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary">No battery data available</Typography>
      </Box>
    );
  }

  // Get latest readings for each vehicle
  const latestReadings = data.map(vehicle => ({
    vehicle: vehicle.device?.plateNumber || vehicle.imei?.slice(-6) || 'Unknown',
    voltage: parseFloat(vehicle.avgVoltage),
    status: vehicle.healthStatus,
    minVoltage: vehicle.minVoltage,
    maxVoltage: vehicle.maxVoltage
  }));

  // ✅ FIX: Sample fewer points for cleaner chart (every 10th reading)
  const allReadings = data[0]?.readings || [];
  const sampledReadings = allReadings.filter((_: any, idx: number) => idx % 10 === 0).slice(-20);
  
  const trendData = sampledReadings.map((r: any, idx: number) => ({
    index: idx,
    voltage: r.voltage,
    ignition: r.ignition
  }));

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'healthy': return 'success';
      case 'low': return 'warning';
      case 'critical': return 'error';
      case 'overcharging': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (voltage: number) => {
    if (voltage < 12.0) return <Battery20 />;
    if (voltage < 12.4) return <Battery50 />;
    if (voltage < 13.0) return <Battery80 />;
    return <BatteryFull />;
  };

  const avgVoltage = latestReadings.length > 0
    ? (latestReadings.reduce((sum, r) => sum + r.voltage, 0) / latestReadings.length).toFixed(2)
    : '0.00';

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Summary */}
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {avgVoltage}V
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Average Battery Voltage
        </Typography>
      </Box>

      {/* Status Chips */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {latestReadings.slice(0, 2).map((reading, idx) => (
          <Chip
            key={idx}
            icon={getStatusIcon(reading.voltage)}
            label={`${reading.vehicle}: ${reading.voltage}V`}
            color={getStatusColor(reading.status) as any}
            size="small"
          />
        ))}
      </Stack>

      {/* Voltage Trend Chart */}
      {trendData.length > 0 && (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            Voltage Trend (Latest Vehicle)
          </Typography>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="index" 
                axisLine={false} 
                tickLine={false}
                tick={{ fontSize: 10 }}
                hide
              />
              <YAxis 
                domain={[11.5, 14.5]} 
                axisLine={false} 
                tickLine={false}
                tick={{ fontSize: 11 }}
              />
              <Tooltip 
                formatter={(value: number) => [`${value.toFixed(2)}V`, 'Voltage']}
                labelFormatter={() => 'Reading'}
              />
              <Line 
                type="monotone" 
                dataKey="voltage" 
                stroke="#2196f3" 
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Health Summary */}
      <Box sx={{ pt: 1, borderTop: '1px solid #f0f0f0' }}>
        <Typography variant="caption" color="textSecondary">
          Healthy: {data.filter(d => d.healthStatus === 'healthy').length} • 
          Needs Attention: {data.filter(d => ['low', 'critical', 'overcharging'].includes(d.healthStatus)).length}
        </Typography>
      </Box>
    </Box>
  );
};

export default RealBatteryHealthWidget;